import { cn } from "@lib/cn"
import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react"

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, hint, id, ...props }, ref) => {
    const generatedId = id ?? props.name
    return (
      <div className="space-y-1">
        {label && (
          <label htmlFor={generatedId} className="text-ink-muted block text-sm font-medium">
            {label}
          </label>
        )}
        <input
          id={generatedId}
          ref={ref}
          aria-invalid={!!error}
          className={cn(
            // 44px and 16px on a phone (touch target; iOS zooms on focus under
            // 16px), the old 38px / 14px from md up. `rounded-control` is the
            // token; `rounded-lg` was a literal.
            "rounded-control min-h-11 w-full border px-3 py-2 text-base md:min-h-0 md:text-sm",
            "focus:ring-2 focus:outline-none",
            error ? "border-danger" : "border-border",
            className,
          )}
          {...props}
        />
        {error ? (
          <p className="text-danger-ink text-xs" role="alert">
            {error}
          </p>
        ) : hint ? (
          <p className="text-ink-subtle text-xs">{hint}</p>
        ) : null}
      </div>
    )
  },
)
Input.displayName = "Input"
